import { Badge } from "./ui/Badge";
import { Card, CardBody, CardHeader } from "./ui/Card";
import { PlainTextBlock } from "./PlainTextBlock";
import { TopicCard } from "./TopicCard";
import { briefingView, sectionLabel } from "../copy/sq";
import { formatDate, formatDateTime } from "../lib/date";
import type { BriefingDetail } from "../types/api";

const SECTION_ORDER = [
  "1. Top Education Signals",
  "2. Documents To Review",
  "3. Data Watch",
  "4. Investigation Opportunities",
  "5. Follow-Up Tracking",
];

/**
 * Renders one full briefing exactly as stored — the same five top-level
 * sections the pipeline produces, in canonical order, each visually
 * distinguished. Section 4 renders via its structured `topics` (the one
 * section with real per-item shape — see
 * ai/parsers/daily_briefing_parser.py) rather than duplicating its raw
 * prose; every other section renders its stored text as-is.
 */
export function BriefingView({ briefing }: { briefing: BriefingDetail }) {
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{formatDate(briefing.briefing_date)}</p>
          {briefing.title ? <h2 className="mt-0.5 font-serif text-xl font-semibold text-ink-950">{briefing.title}</h2> : null}
          <p className="mt-1 text-xs text-ink-400">Generated {formatDateTime(briefing.generated_at)}</p>
        </div>
        <Badge tone={briefing.delivered ? "good" : "neutral"}>{briefing.delivered ? briefingView.delivered : briefingView.notDelivered}</Badge>
      </header>

      {briefing.missing_sections.length > 0 ? (
        <div className="rounded-lg border border-signal-warn-bg bg-signal-warn-bg/50 px-4 py-2.5 text-sm text-signal-warn">
          {briefingView.missingSections(briefing.missing_sections.length)}
        </div>
      ) : null}

      {SECTION_ORDER.map((sectionKey) => {
        const isInvestigationSection = sectionKey === "4. Investigation Opportunities";
        const text = briefing.sections[sectionKey];

        if (isInvestigationSection) {
          return (
            <section key={sectionKey} aria-labelledby="section-4-heading">
              <h3 id="section-4-heading" className="mb-3 font-serif text-lg font-semibold text-ink-950">
                {sectionLabel(sectionKey)}
              </h3>
              {briefing.topics.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {briefing.topics.map((topic) => (
                    <TopicCard key={topic.slot || topic.title} topic={topic} />
                  ))}
                </div>
              ) : text ? (
                <Card>
                  <CardBody>
                    <PlainTextBlock text={text} />
                  </CardBody>
                </Card>
              ) : (
                <p className="text-sm italic text-ink-400">{briefingView.notProduced}</p>
              )}
            </section>
          );
        }

        return (
          <section key={sectionKey}>
            <Card>
              <CardHeader>
                <h3 className="font-serif text-lg font-semibold text-ink-950">{sectionLabel(sectionKey)}</h3>
              </CardHeader>
              <CardBody>{text ? <PlainTextBlock text={text} /> : <p className="text-sm italic text-ink-400">{briefingView.notProduced}</p>}</CardBody>
            </Card>
          </section>
        );
      })}
    </div>
  );
}
